import { Avatar, Group, Menu, Stack, Text, UnstyledButton } from "@mantine/core";
import { IconChevronRight, IconLogout, IconUser, IconUserPause } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { authClient } from "~/lib/auth-client";
import { warningNotification } from "~/lib/notifications";
import { useDesktopSidebar } from "~/stores/ui";
import { ProfilePicture } from "./profile-picture";
import classes from "./profile-section.module.css";

export function ProfileSection() {
  const isExpanded = useDesktopSidebar((state) => state.isExpanded);
  if (isExpanded) return <ExpandedProfileSection />;
  return <CollapsedProfileSection />;
}

function CollapsedProfileSection() {
  const session = authClient.useSession();
  if (!session.data?.user) return null;

  return (
    <UserMenu>
      <UnstyledButton className={classes.userCollapsed}>
        <Avatar radius="xl" name={session.data.user.name} />
      </UnstyledButton>
    </UserMenu>
  );
}

function ExpandedProfileSection() {
  const session = authClient.useSession();
  if (!session.data?.user) return null;

  return (
    <UserMenu>
      <UnstyledButton className={classes.userExpanded}>
        <Group justify="space-between">
          <Group justify="flex-start">
            {/* <Avatar radius="xl" name={session.data.user.name} /> */}
            <ProfilePicture user={session.data.user} radius="xl" />
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                {session.data.user.name}
              </Text>
              <Text c="dimmed" size="xs">
                {session.data.user.email}
              </Text>
            </Stack>
          </Group>
          <IconChevronRight size={14} stroke={1.5} />
        </Group>
      </UnstyledButton>
    </UserMenu>
  );
}

function UserMenu(props: { children: ReactNode }) {
  const navigate = useNavigate();

  function notImplemented() {
    warningNotification("Not implemented", "This feature is not implemented yet.");
  }

  async function signOut() {
    await authClient.signOut();
    window.location.reload();
  }

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>{props.children}</Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => navigate({ to: "/profile" })} leftSection={<IconUser size={14} />}>
          Profile
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item onClick={signOut} color="red" leftSection={<IconLogout size={14} />}>
          Log Out
        </Menu.Item>
        <StopImpersonating />
      </Menu.Dropdown>
    </Menu>
  );
}

export function StopImpersonating() {
  const session = authClient.useSession();

  const userId = session.data?.user.id;
  const impersonatedBy = session.data?.session.impersonatedBy as string | number | null | undefined;

  const stopImpersonating = useMutation({
    mutationFn: async () => {
      await authClient.admin.stopImpersonating();
    },
    mutationKey: ["stop-impersonating", userId, impersonatedBy],
    onSuccess: async () => {
      window.location.reload();
    },
  });

  if (session.isPending) return null;
  if (!impersonatedBy) return null;

  return (
    <Menu.Item color="red" leftSection={<IconUserPause size={14} />} onClick={() => stopImpersonating.mutate()}>
      Stop Impersonating
    </Menu.Item>
  );
}
